import { Pin, PinOff, Trash2, User } from 'lucide-react';
import { useDeleteNote, useTogglePin, type ApprenantNote } from '../hooks/useApprenantNotes.js';

const CATEGORY_STYLE: Record<string, { label: string; color: string }> = {
  general: { label: 'Général', color: 'bg-neutral-100 text-neutral-700' },
  commercial: { label: 'Commercial', color: 'bg-sky-100 text-sky-700' },
  pedagogique: { label: 'Pédagogique', color: 'bg-violet-100 text-violet-700' },
  admin: { label: 'Admin', color: 'bg-amber-100 text-amber-700' },
  alerte: { label: 'Alerte', color: 'bg-rose-100 text-rose-700' },
};

interface Props {
  note: ApprenantNote;
  apprenantId: string;
  currentUserEmail: string | null;
  /** Affiche le nom de l'apprenant + son ID (pour la vue cross-apprenants) */
  showApprenantLink?: boolean;
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Composant dédié — ligne d'une note dans la liste.
 * Gère l'action pin + supprimer (uniquement pour l'auteur).
 */
export function NoteRow({ note, apprenantId, currentUserEmail, showApprenantLink }: Props) {
  const canEdit = currentUserEmail && note.author.email === currentUserEmail;
  const deleteMut = useDeleteNote(apprenantId);
  const pinMut = useTogglePin(apprenantId);
  const cat = CATEGORY_STYLE[note.category] ?? CATEGORY_STYLE.general!;

  return (
    <div
      className={`group relative rounded-lg border p-3 transition-colors ${
        note.pinned
          ? 'border-amber-300 bg-amber-50/40'
          : 'border-neutral-200 bg-white hover:bg-neutral-50'
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-neutral-200 text-neutral-600">
          <User className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-neutral-900">
              {note.author.name ?? note.author.email.split('@')[0]}
            </span>
            <span className="text-[10px] text-neutral-500">{formatRelative(note.createdAt)}</span>
            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${cat.color}`}>
              {cat.label}
            </span>
            {note.pinned && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700">
                <Pin className="h-2.5 w-2.5" />
                Épinglée
              </span>
            )}
            {showApprenantLink && (
              <a
                href={`/apprenants/${note.apprenantId}`}
                className="text-primary-600 ml-auto text-[10px] font-semibold hover:underline"
              >
                Ouvrir fiche →
              </a>
            )}
          </div>
          <div className="whitespace-pre-wrap text-xs text-neutral-700">{note.content}</div>
        </div>
      </div>

      {canEdit && (
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onClick={() => pinMut.mutate({ noteId: note.id, pinned: !note.pinned })}
            disabled={pinMut.isPending}
            title={note.pinned ? 'Désépingler' : 'Épingler'}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-200 hover:text-amber-600"
          >
            {note.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirm('Supprimer cette note ?')) deleteMut.mutate(note.id);
            }}
            disabled={deleteMut.isPending}
            title="Supprimer"
            className="rounded p-1 text-neutral-500 hover:bg-rose-100 hover:text-rose-600"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
